import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Loader2 } from 'lucide-react';

const UnsavedGuardContext = createContext(null);

export const useUnsavedGuard = () => {
  const ctx = useContext(UnsavedGuardContext);
  if (!ctx) {
    // Defensive default — pages should still render even if provider missing.
    return {
      setGuard: () => {},
      clearGuard: () => {},
      requestExit: (action) => action && action(),
    };
  }
  return ctx;
};

/**
 * Provider that exposes:
 *   setGuard({ onSaveDraft })   — page registers an "unsaved progress" handler
 *   clearGuard()                — page clears its registration (on unmount or save)
 *   requestExit(action)         — call instead of running `action` directly;
 *                                 if a guard is active, shows the confirm dialog
 *                                 with 3 options. If no guard, runs action immediately.
 *
 * The dialog renders inside this provider so it's available app-wide.
 */
export const UnsavedGuardProvider = ({ children }) => {
  const guardRef = useRef(null); // { onSaveDraft: async () => void }
  const pendingActionRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const setGuard = useCallback((guard) => {
    guardRef.current = guard;
  }, []);

  const clearGuard = useCallback(() => {
    guardRef.current = null;
  }, []);

  const requestExit = useCallback((action) => {
    if (guardRef.current) {
      pendingActionRef.current = action;
      setIsOpen(true);
    } else if (typeof action === 'function') {
      action();
    }
  }, []);

  const runPending = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    guardRef.current = null;
    setIsOpen(false);
    if (typeof action === 'function') action();
  };

  const handleSaveAndExit = async () => {
    setSaving(true);
    try {
      if (guardRef.current?.onSaveDraft) {
        await guardRef.current.onSaveDraft();
      }
    } catch (_) {
      // Best-effort — proceed with exit even if save fails (user already saw the toast).
    } finally {
      setSaving(false);
      runPending();
    }
  };

  const handleExitWithoutSaving = () => {
    runPending();
  };

  const handleCancel = () => {
    pendingActionRef.current = null;
    setIsOpen(false);
  };

  return (
    <UnsavedGuardContext.Provider value={{ setGuard, clearGuard, requestExit }}>
      {children}
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <DialogContent data-testid="unsaved-guard-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="unsaved-guard-title">Sair sem salvar?</DialogTitle>
            <DialogDescription data-testid="unsaved-guard-body">
              Você tem um documento em andamento. Deseja salvar o rascunho antes de sair?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={saving}
              data-testid="unsaved-guard-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleExitWithoutSaving}
              disabled={saving}
              data-testid="unsaved-guard-exit-without-save"
            >
              Sair sem Salvar
            </Button>
            <Button
              type="button"
              onClick={handleSaveAndExit}
              disabled={saving}
              data-testid="unsaved-guard-save-and-exit"
              className="bg-primary hover:bg-primary-hover"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Rascunho e Sair'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UnsavedGuardContext.Provider>
  );
};
